import React, { useEffect } from 'react';
import { Heart } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Favorite Button Component
 * Uses FavoritesContext for centralized state management (avoids N+1 API calls)
 */
export default function FavoriteButton({ 
  sessionId, 
  initialIsFavorite = false,
  size = 'default', // 'small', 'default', 'large'
  showLabel = false,
  onToggle = null,
  className = ''
}) {
  const { isAuthenticated } = useAuth();
  const { isFavorite, queueCheck, toggleFavorite, loading } = useFavorites();
  
  const isFav = isFavorite(sessionId);

  // Queue this session for batch checking on mount
  useEffect(() => {
    if (isAuthenticated && sessionId) {
      queueCheck(sessionId);
    }
  }, [isAuthenticated, sessionId, queueCheck]);

  const handleToggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.info('Connectez-vous pour ajouter aux favoris');
      return;
    }

    const success = await toggleFavorite(sessionId);
    if (success) {
      const newState = !isFav;
      if (newState) {
        toast.success('Ajouté aux favoris ! Vous serez notifié des places disponibles.');
      } else {
        toast.success('Retiré des favoris');
      }
      if (onToggle) onToggle(newState);
    } else {
      toast.error('Erreur lors de la mise à jour des favoris');
    }
  };

  // Size variants
  const sizeClasses = {
    small: 'w-8 h-8',
    default: 'w-10 h-10',
    large: 'w-12 h-12',
  };

  const iconSizes = {
    small: 'w-4 h-4',
    default: 'w-5 h-5',
    large: 'w-6 h-6',
  };

  return (
    <button
      onClick={handleToggle}
      disabled={loading}
      className={`
        ${sizeClasses[size]}
        flex items-center justify-center gap-2
        rounded-full transition-all duration-200
        ${isFav 
          ? 'bg-red-500/20 text-red-500 hover:bg-red-500/30' 
          : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-red-400'
        }
        ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      title={isFav ? 'Retirer des favoris' : 'Ajouter aux favoris'}
      data-testid={`favorite-btn-${sessionId}`}
    >
      <Heart 
        className={`
          ${iconSizes[size]}
          transition-all duration-200
          ${isFav ? 'fill-current' : ''}
          ${loading ? 'animate-pulse' : ''}
        `}
      />
      {showLabel && (
        <span className="text-sm font-medium">
          {isFav ? 'Favori' : 'Ajouter'}
        </span>
      )}
    </button>
  );
}

/**
 * Favorite Badge - Shows count of favorites
 */
export function FavoritesBadge({ className = '' }) {
  const { isAuthenticated } = useAuth();
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (isAuthenticated) {
      fetchCount();
    }
  }, [isAuthenticated]);

  const fetchCount = async () => {
    try {
      const response = await fetch(`${API_URL}/api/favorites/count`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setCount(data.count);
      }
    } catch (error) {
      console.error('Error fetching favorites count:', error);
    }
  };

  if (!isAuthenticated || count === 0) return null;

  return (
    <span 
      className={`
        absolute -top-1 -right-1 
        min-w-[18px] h-[18px] 
        flex items-center justify-center
        text-[11px] font-bold
        bg-red-500 text-white 
        rounded-full
        ${className}
      `}
      data-testid="favorites-badge"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}
